import React from 'react';
import { Pressable, type ViewStyle, View } from 'react-native';
import { Row, Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useClickOutside } from 'app/hooks/use-click-outside';
import { type ListingsSortBy } from 'app/network/listing';
import { useScopedTranslation } from 'app/hooks/i18n';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArrowsSort, IconCheck, IconChevronDown } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isMobileNative } from 'app/utils/web-helpers';
import { useClickOutside as useRnClickOutside } from 'react-native-click-outside';
const theme = createDSTheme();

interface SortDropdownProps {
  sortBy: ListingsSortBy;
  sortOptions: ListingsSortBy[];
  onSort: (sortBy: ListingsSortBy) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export const SortDropdown = ({ sortBy, sortOptions, onSort, isOpen, onToggle }: SortDropdownProps) => {
  const { t } = useScopedTranslation();
  const menuRef = React.useRef(null);
  const [hoveredSortBy, setHoveredSortBy] = React.useState<ListingsSortBy | undefined>(undefined);
  const [isTriggerHovered, setIsTriggerHovered] = React.useState(false);

  useClickOutside([menuRef], (clickedOutside) => {
    if (clickedOutside && isOpen) {
      onToggle();
    }
  });

  const asRef = useRnClickOutside(() => {
    if (isOpen) {
      onToggle();
    }
  });

  const ref = isMobileNative ? asRef : menuRef;

  const handleSort = (by: ListingsSortBy) => {
    onSort(by);
    onToggle();
  };

  const getSortItemStyle = (by: ListingsSortBy): ViewStyle => ({
    minHeight: 38,
    borderRadius: 9,
    paddingHorizontal: 12,
    paddingVertical: 8,
    width: '100%',
    backgroundColor:
      sortBy === by
        ? theme.colors['surface.tertiary-neutral']
        : hoveredSortBy === by
          ? theme.colors['surface.tertiary-neutral']
          : 'transparent',
  });

  return (
    <Row innerRef={ref} position="relative">
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        onHoverIn={() => setIsTriggerHovered(true)}
        onHoverOut={() => setIsTriggerHovered(false)}
        onPress={onToggle}
        style={({ pressed }) => ({
          minHeight: 36,
          paddingLeft: 12,
          paddingRight: 8,
          paddingVertical: 6,
          backgroundColor: isOpen || isTriggerHovered ? theme.colors.white : theme.colors['surface.tertiary-neutral'],
          borderRadius: theme.borderRadii['radius.full'],
          borderWidth: 1,
          borderColor: isOpen ? theme.colors['border.focused'] : theme.colors['border.secondary'],
          boxShadow: isOpen || isTriggerHovered ? '0px 2px 8px rgba(0, 0, 0, 0.06)' : undefined,
          opacity: pressed ? 0.82 : 1,
        })}
      >
        <Row alignItems="center" gap="spacing.general.x1">
          <IconArrowsSort strokeWidth={ICON_STROKE_WIDTH} />
          <Text color="text.primary" fontSize={14} fontWeight="700" lineHeight={18} numberOfLines={1}>
            {t(`sortBy.${sortBy as string}`)}
          </Text>
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: 'center',
              justifyContent: 'center',
              transform: [{ rotate: isOpen ? '180deg' : '0deg' }],
              backgroundColor: isOpen ? theme.colors['surface.tertiary-neutral'] : 'transparent',
            }}
          >
            <IconChevronDown color={theme.colors['text.secondary']} size={15} strokeWidth={ICON_STROKE_WIDTH + 0.25} />
          </View>
        </Row>
      </Pressable>

      {isOpen ? (
        <View
          style={{
            position: 'absolute',
            top: 44,
            right: 0,
            zIndex: 9999,
            minWidth: 220,
          }}
        >
          <Column
            gap="spacing.general.x1"
            padding="spacing.general.x1"
            style={{
              backgroundColor: theme.colors.white,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: theme.colors['border.secondary'],
              boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.08)',
            }}
          >
            {sortOptions.map((by) => (
              <Pressable
                key={by}
                onHoverIn={() => setHoveredSortBy(by)}
                onHoverOut={() => setHoveredSortBy(undefined)}
                onPress={() => handleSort(by)}
                style={getSortItemStyle(by)}
              >
                <Row alignItems="center" justifyContent="space-between">
                  <Text
                    color={sortBy === by || hoveredSortBy === by ? 'text.primary' : 'text.secondary'}
                    flex={1}
                    fontSize={14}
                    fontWeight={sortBy === by ? '700' : '500'}
                    lineHeight={18}
                    textAlign="left"
                  >
                    {t(`sortBy.${by as string}`)}
                  </Text>
                  {sortBy === by ? (
                    <IconCheck color={theme.colors['text.primary']} size={18} strokeWidth={ICON_STROKE_WIDTH + 0.5} />
                  ) : null}
                </Row>
              </Pressable>
            ))}
          </Column>
        </View>
      ) : null}
    </Row>
  );
};
